import React from 'react'
import { getExternalLinkTargetProps } from 'src/utils/externalLink'
import cx from 'classnames'
import { Link } from 'gatsby'
import BlockContent from '@sanity/block-content-to-react'

import { FeatureCard } from 'src/components/inline/featureCard'
import { ButtonBlock } from 'src/components/inline/buttonBlock'
import { Experiment } from 'src/components/global/experiment'
import { NewsletterBlock } from 'src/components/inline/newsletterBlock'
import { ColumnText } from 'src/components/inline/columnText'

import { handleSlug, buildLinkWithAnchor } from 'src/utils/utils'
import { CtaAnalyticsContext } from 'src/utils/ctaTracking'
import { PortableTextFontSize } from 'src/utils/portableTextFontSize'
import {
  PortableTextColor,
  PortableTextFontFamily,
  PortableTextStrikethrough,
} from 'src/utils/portableTextMarks'

import { SmallArrow } from 'src/components/svgs'
import { FeatureCardGroup } from 'src/components/inline/FeatureCardGroup'
import { useSanity } from 'src/context/SanityContext'

export const createSerializer = (analyticsContext?: CtaAnalyticsContext) => ({
  marks: {
    'strike-through': (props: { children: any }) => (
      <PortableTextStrikethrough>{props.children}</PortableTextStrikethrough>
    ),
    textColor: (props: { children: any; mark: any }) => (
      <PortableTextColor color={props.mark?.color}>
        {props.children}
      </PortableTextColor>
    ),
    annotationLink: (props: { children: any; mark: any }) => {
      const href = handleSlug(
        props.mark.reference._type,
        props.mark.reference.content.main.slug.current
      )

      return (
        <Link className="underline cpink" to={href}>
          {props.children}
        </Link>
      )
    },
    internalLink: (props: { children: any; mark: any }) => {
      if (props.mark.reference && props.mark.reference.content) {
        const linkType = props.mark.linkType
        const isPlain = linkType === 'plain'
        const href = handleSlug(
          props.mark.reference._type,
          props.mark.reference.content.main.slug.current
        )

        if (linkType === 'text-arrow') {
          return (
            <Link className={cx(' f jcs aic')} to={href}>
              <span
                className={cx('', {
                  'small bold underline ls1':
                    linkType === 'text-arrow',
                })}
              >
                {props.children}
              </span>

              {linkType === 'text-arrow' && <SmallArrow />}
            </Link>
          )
        } else {
          const linkClassName = cx({
            'inline-block': !isPlain,
            'ls1 button s14 my1 no-underline': linkType === 'button',
            'small bold underline ls1': linkType === 'text',
          })

          return (
            <Link
              id={
                linkType === 'one-trust-consent-modal'
                  ? 'ot-sdk-btn'
                  : undefined
              }
              className={linkClassName || undefined}
              to={href}
            >
              {props.children}
            </Link>
          )
        }
      } else {
        return <span>{props.children}</span>
      }
    },
    externalLink: (props: { children: any; mark: any }) => {
      const linkType = props.mark.linkType
      const isPlain = linkType === 'plain'
      const isTextArrow = linkType === 'text-arrow'
      const linkClassName = cx({
        'small bold underline': !isPlain,
        'f jcs aic': isTextArrow,
      })

      return (
        <a
          id={
            linkType === 'one-trust-consent-modal'
              ? 'ot-sdk-btn'
              : undefined
          }
          className={linkClassName || undefined}
          href={props.mark.url}
          {...getExternalLinkTargetProps(props.mark.url)}
        >
          {props.children}
          {isTextArrow && <SmallArrow />}
        </a>
      )
    },
    highlight: (props: { children: any }) => {
      return (
        <span className="bg-primary-3 mono ss14 sm16 caps px1 py05 br5 cw">
          {props.children}
        </span>
      )
    },
    alignCenter: (props: { children: any }) => {
      return (
        <span style={{ display: 'block', textAlign: 'center' }}>
          {props.children}
        </span>
      )
    },
    fontSize14: (props: { children: any }) => {
      return (
        <PortableTextFontSize size={14}>{props.children}</PortableTextFontSize>
      )
    },
    fontSize16: (props: { children: any }) => {
      return (
        <PortableTextFontSize size={16}>{props.children}</PortableTextFontSize>
      )
    },
    fontSize18: (props: { children: any }) => {
      return (
        <PortableTextFontSize size={18}>{props.children}</PortableTextFontSize>
      )
    },
    fontSize24: (props: { children: any }) => {
      return (
        <PortableTextFontSize size={24}>{props.children}</PortableTextFontSize>
      )
    },
    fontSize32: (props: { children: any }) => {
      return (
        <PortableTextFontSize size={32}>{props.children}</PortableTextFontSize>
      )
    },
    fontSize40: (props: { children: any }) => {
      return (
        <PortableTextFontSize size={40}>{props.children}</PortableTextFontSize>
      )
    },
    fontSize48: (props: { children: any }) => {
      return (
        <PortableTextFontSize size={48}>{props.children}</PortableTextFontSize>
      )
    },
    fontSerif: (props: { children: any }) => {
      return (
        <PortableTextFontFamily family="serif">
          {props.children}
        </PortableTextFontFamily>
      )
    },
    fontSans: (props: { children: any }) => {
      return (
        <PortableTextFontFamily family="sans">
          {props.children}
        </PortableTextFontFamily>
      )
    },
  },
  types: {
    featureCard: (props: { node: {} }) => {
      return <FeatureCard data={props?.node} />
    },
    featureCardGroup: (data: any) => {
      return <FeatureCardGroup data={data?.node} />
    },
    newsletterBlock: (props: { node: {} }) => {
      return <NewsletterBlock data={props.node} />
    },
    divider: (props: { node: {} }) => {
      return <div className={cx('divider x', props.node.dividerType)} />
    },
    cta: (props: { node: {} }) => {
      return <span>cta</span>
    },

    columnText: (props: { none: {} }) => {
      return (
        <div className="f jcb fw ais">
          {props.node.items?.map(item => (
            <ColumnText {...item} key={item._key} />
          ))}
        </div>
      )
    },
    buttonBlock: (props: { node: {} }) => {
      return <ButtonBlock data={props.node} analyticsContext={analyticsContext} />
    },
    experiment: (props: { node: {} }) => {
      return <Experiment data={props.node} />
    },
    checkList: (props: {
      node: {
        backgroundColor: string
        checkboxSize: number
        color: string
        items: string[]
        showDivider: boolean
        textSize: number
      }
    }) => {
      return (
        <ul
          className="check__list"
          style={{
            lineHeight: `${props.node.checkboxSize + 16}px`,
          }}
        >
          {props.node.items?.map((item, i) => (
            <li
              key={i}
              className={cx('check rel f', {
                showDivider: props.node.showDivider,
              })}
              style={{
                fontSize: props.node.textSize,
                paddingLeft: props.node.checkboxSize + 8,
              }}
            >
              <span
                className={`check__mark abs f jcc aic left ${props.node.backgroundColor}`}
                style={{
                  width: props.node.checkboxSize,
                  height: props.node.checkboxSize,
                }}
              >
                <svg width="16" height="13" viewBox="0 0 16 13" fill="none">
                  <path
                    d="M1.46094 7.06602L6.01805 11.4921L14.5379 1.03052"
                    stroke={props.node.color}
                    strokeWidth="1.56923"
                  />
                </svg>
              </span>
              {item}
            </li>
          ))}
        </ul>
      )
    },
    image: (props: { node: { asset: { url: string }; alt: string } }) => {
      const { getImageUrl } = useSanity()

      const imageUrl = getImageUrl(props.node.asset)
      return (
        <img
          src={imageUrl || ''}
          alt={props.node.alt || 'Image'}
          className="responsive-image"
        />
      )
    },
    block: (props: {
      children: any[]
      node: {
        style?: string
      }
    }) => {
      const { style } = props.node

      const titleIDs = {
        'Electronic Communications': 'electronic-communications',
        'Purchase of Membership': 'purchase-of-membership',
        'Cancellation or Suspension of Membership':
          'cancellation-or-suspension-of-membership',
        'Cancellation by You': 'cancellation-by-you',
        'Membership Cancellation': 'membership-cancellation',
      }

      const childKey = props.children[0] as keyof typeof titleIDs
      const id = titleIDs[childKey] ?? undefined

      switch (style) {
        case 'h1':
          return <h1 className="portable-heading--h1">{props.children}</h1>
        case 'layout-h1-plus':
          return (
            <h1 className="portable-heading--h1 layout-h1-plus" id={id}>
              {props.children}
            </h1>
          )
        case 'large':
          return <p className="ss20 sm25">{props.children}</p>
        case 'small':
          return <p className="text-small">{props.children}</p>
        case 'h1-l':
          return (
            <h1 className="h1 h1-large portable-heading--h1" id={id}>
              {props.children}
            </h1>
          )
        case 'h2':
          return (
            <h2 className="portable-heading--h2" id={id}>
              {props.children}
            </h2>
          )
        case 'h4':
          return (
            <h4 className="portable-heading--h4" id={id}>
              {props.children}
            </h4>
          )
        case 'h5':
          return (
            <h5 className="caps portable-heading--h5 w100">{props.children}</h5>
          )
        case 'h3':
          return <h3 className="portable-heading--h3">{props.children}</h3>
        default:
          // @ts-ignore
          return BlockContent.defaultSerializers.types.block(props)
      }
    },
  },
})

export const Serializer = createSerializer()
